<template>
  <header class="mast">
    <div class="mast-in">
      <router-link :to="homeLink" class="wordmark" aria-label="Thinking of U — home">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="10.6" fill="none" stroke="currentColor" stroke-width="1.4" />
          <rect x="6" y="8.4" width="12" height="8" fill="none" stroke="currentColor" stroke-width="1.4" transform="rotate(-6 12 12)" />
          <path d="M6.4 9 L12 13.4 L17.6 9" fill="none" stroke="currentColor" stroke-width="1.4" transform="rotate(-6 12 12)" />
        </svg>
        <span>Thinking of U</span>
      </router-link>

      <!-- A recipient's own page: they are not a member and have no account.
           Nothing of the member's app belongs here. -->
      <span v-if="bare" class="aside">Private to you</span>

      <template v-else>
        <nav v-if="!user" class="out" aria-label="Site">
          <router-link to="/about">About</router-link>
          <router-link to="/create-postcard">Make a card</router-link>
        </nav>

        <div class="right">
          <button v-if="cartItemCount > 0" type="button" class="cart" @click="toggleCart" :aria-label="`Cart, ${cartItemCount} item${cartItemCount === 1 ? '' : 's'}`">
            <span class="lab-ish">Cart</span><span class="count">{{ cartItemCount }}</span>
          </button>

          <router-link v-if="!user" to="/login" class="signin">Sign in</router-link>

          <div v-else class="who" ref="menu">
            <button type="button" class="avatar" :aria-expanded="menuOpen ? 'true' : 'false'" aria-haspopup="menu" @click="menuOpen = !menuOpen" :title="user.displayName || user.email">
              <span aria-hidden="true">{{ initial }}</span><span class="sr">Your account</span>
            </button>
            <div v-if="menuOpen" class="menu" role="menu">
              <span class="menu-who">{{ user.displayName || user.email }}</span>
              <router-link to="/profile" role="menuitem" @click="menuOpen = false">Profile</router-link>
              <router-link to="/membership" role="menuitem" @click="menuOpen = false">Membership</router-link>
              <button type="button" role="menuitem" class="signout" @click="signOutNow">Sign out</button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Signed in, the app IS the assistant: the desk first, then the people
         it watches, the gifts it found, and the card you make yourself. -->
    <nav v-if="user && !bare" class="tabs" aria-label="Your desk">
      <router-link to="/assistant" class="tab">The desk</router-link>
      <router-link to="/my-people" class="tab">People</router-link>
      <router-link to="/gifts" class="tab">Gifts</router-link>
      <router-link to="/create-postcard" class="tab">Make a card</router-link>
    </nav>
  </header>
</template>

<script>
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/firebase';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AppHeader',
  data() {
    return {
      user: null,
      menuOpen: false,
    };
  },
  computed: {
    ...mapGetters(['cartItemCount']),
    bare() {
      return Boolean(this.$route.meta && this.$route.meta.bare);
    },
    homeLink() {
      return this.user && !this.bare ? '/assistant' : '/';
    },
    initial() {
      const s = (this.user && (this.user.displayName || this.user.email)) || '?';
      return s.trim().charAt(0).toUpperCase();
    },
  },
  watch: {
    $route() {
      this.menuOpen = false;
    },
  },
  created() {
    onAuthStateChanged(auth, (user) => {
      this.user = user || null;
    });
  },
  mounted() {
    document.addEventListener('click', this.closeOnOutside);
    document.addEventListener('keydown', this.closeOnEscape);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeOnOutside);
    document.removeEventListener('keydown', this.closeOnEscape);
  },
  methods: {
    ...mapActions(['toggleCart']),
    closeOnOutside(e) {
      if (this.menuOpen && this.$refs.menu && !this.$refs.menu.contains(e.target)) this.menuOpen = false;
    },
    closeOnEscape(e) {
      if (e.key === 'Escape') this.menuOpen = false;
    },
    async signOutNow() {
      this.menuOpen = false;
      try {
        await signOut(auth);
        this.$store.dispatch('logout');
        this.$router.push('/login');
      } catch (error) {
        console.error('Error logging out:', error);
      }
    },
  },
};
</script>

<style scoped>
/* the header sits on legacy pages too, so it carries its own helpers */
.sr { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.mast {
  background: var(--kraft);
  border-bottom: 1.5px solid var(--ink);
  font-family: var(--text);
  text-align: left;
  color: var(--ink);
}
.mast-in {
  max-width: var(--page);
  margin: 0 auto;
  padding: 14px var(--gut) 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 22px;
}
.wordmark {
  display: flex;
  align-items: center;
  gap: 9px;
  font-family: var(--display);
  font-size: 1.12rem;
  color: var(--ink);
  text-decoration: none;
  white-space: nowrap;
  letter-spacing: -.01em;
}
.wordmark svg { width: 24px; height: 24px; flex: none; color: var(--red); }
.wordmark:hover { color: var(--ink); }

.aside, .out a, .signin, .lab-ish, .menu-who {
  font-family: var(--mono);
  font-size: .64rem;
  font-weight: 500;
  letter-spacing: .16em;
  text-transform: uppercase;
}
.aside { margin-left: auto; color: var(--green); }

.out { display: flex; gap: 18px; margin-left: auto; }
.out a, .signin { color: var(--ink-2); text-decoration: none; }
.out a:hover, .signin:hover, .out a.router-link-active { color: var(--ink); text-decoration: underline; text-underline-offset: 4px; }

.right { display: flex; align-items: center; gap: 14px; margin-left: auto; }
.out + .right { margin-left: 0; }
.signin { border: 1.5px solid var(--ink); padding: 8px 12px; color: var(--ink); }
.signin:hover { background: var(--sunk); text-decoration: none; }

.cart {
  appearance: none; display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  background: transparent; color: var(--ink); border: 1px solid var(--rule-2); border-radius: 0;
  padding: 7px 10px; margin: 0;
}
.cart:hover { background: var(--sunk); border-color: var(--ink); }
.cart .count {
  font-family: var(--mono); font-size: .7rem; font-weight: 600; min-width: 1.4em; text-align: center;
  color: var(--red-ink);
}

.who { position: relative; }
.avatar {
  appearance: none; cursor: pointer; width: 36px; height: 36px; border-radius: 50%; padding: 0; margin: 0;
  display: grid; place-items: center; background: var(--sheet); color: var(--ink);
  border: 1.5px solid var(--ink); font-family: var(--display); font-size: 1rem; line-height: 1;
}
.avatar:hover, .avatar[aria-expanded="true"] { background: var(--sunk); color: var(--ink); }
.avatar:focus-visible, .cart:focus-visible, .menu a:focus-visible, .menu button:focus-visible, .tab:focus-visible, .wordmark:focus-visible {
  outline: 2.5px solid var(--red); outline-offset: 3px;
}
.menu {
  position: absolute; right: 0; top: calc(100% + 8px); z-index: 60; min-width: 200px;
  background: var(--sheet); border: 1.5px solid var(--ink); display: flex; flex-direction: column;
  box-shadow: 0 10px 24px -14px rgba(20, 16, 26, .45);
}
.menu-who { color: var(--ink-3); padding: 12px 14px 10px; border-bottom: 1px solid var(--rule); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.menu a, .menu .signout {
  appearance: none; display: block; text-align: left; padding: 10px 14px; margin: 0; border: 0; border-radius: 0;
  background: none; color: var(--ink); font-family: var(--text); font-size: .92rem; text-decoration: none; cursor: pointer;
}
.menu a:hover, .menu .signout:hover { background: var(--sunk); color: var(--ink); }
.menu .signout { border-top: 1px solid var(--rule); color: var(--ink-2); }

.tabs {
  max-width: var(--page); margin: 0 auto; padding: 0 var(--gut);
  display: flex; gap: 0 5px; overflow-x: auto; scrollbar-width: none;
}
.tabs::-webkit-scrollbar { display: none; }
.tab {
  flex: none; background: var(--manila); border: 1px solid var(--rule-2); border-bottom: 0;
  padding: 8px 13px 7px; margin-bottom: -1.5px; color: var(--ink-3); border-radius: 4px 4px 0 0;
  font-family: var(--mono); font-size: .66rem; font-weight: 500; letter-spacing: .12em; text-transform: uppercase;
  text-decoration: none; white-space: nowrap; transition: color .14s, background .14s;
}
.tab:hover { color: var(--ink); background: var(--sunk); }
.tab.router-link-active {
  color: var(--ink); background: var(--kraft); border-color: var(--ink);
  border-bottom: 1.5px solid var(--kraft); font-weight: 600;
}
</style>
